import { MediaRender } from '@components/MediaRender'
import { useImageStatus } from '@hooks/useImageStatus'
import { useMediaSize } from '@hooks/useMediaSize'
import { useEffect, useMemo } from 'react'
import {
  AnimatePresence,
  useWindowDimensions,
  View,
  XStack,
  YStack,
} from 'tamagui'
import { NotSupported } from '../../extra_contents/NotSupported'
import { IMedia } from '@dtos/Course'
import { Platform } from 'react-native'

interface IBaseImageViewerProps {
  media: IMedia | null | undefined
  fileUrl?: string
  isLoading?: boolean
  shouldShowOverlay?: boolean
}

export function BaseImageViewer({
  media,
  fileUrl,
  isLoading = false,
}: IBaseImageViewerProps) {
  const mediaSize = useMediaSize()
  const isMobile = ['xs', 'sm'].includes(mediaSize)
  const { isHighlightedImage, setIsHighlightedImage, reset } = useImageStatus()
  const { width: windowWidth, height: windowHeight } = useWindowDimensions()

  const shouldShowOverlay =
    isMobile && !(Platform.OS === 'ios' && isHighlightedImage)

  const dynamicHeight = useMemo(() => {
    return isHighlightedImage ? windowHeight : windowHeight - 80
  }, [isHighlightedImage, windowHeight])

  const handleToggleHighlight = () => {
    if (isMobile) {
      setIsHighlightedImage(!isHighlightedImage)
    }
  }

  const onViewPress = Platform.OS === 'ios' ? handleToggleHighlight : undefined

  useEffect(() => {
    reset()
  }, [reset])

  if (!media) {
    return null
  }

  return (
    <View w="100%" h="100%">
      <AnimatePresence>
        <View
          key="space-between-header-and-image"
          w="100%"
          h={isHighlightedImage ? 0 : 80}
          bg="$bg-chrome"
          animation="quick"
          opacity={isHighlightedImage ? 0 : 1}
          flexShrink={0}
        />
        <YStack
          key="image-container"
          w="100%"
          h={dynamicHeight}
          f={1}
          maxHeight={dynamicHeight}
          bg="$bg-surface-raised"
          alignItems="center"
          justifyContent="center"
          animation="quick"
          position="relative"
        >
          {shouldShowOverlay && (
            <XStack
              bg="transparent"
              position="absolute"
              w="100%"
              h="100%"
              top={0}
              left={0}
              right={0}
              bottom={0}
              zIndex={10}
              onPress={handleToggleHighlight}
              pointerEvents="auto"
            />
          )}
          <View
            justifyContent="center"
            overflow="hidden"
            flex={1}
            maxWidth={windowWidth}
            maxHeight={windowHeight}
            alignItems="center"
            onPress={onViewPress}
          >
            <MediaRender
              media={media}
              isImageVariant
              isExpandedViewPage
              fileUrl={fileUrl}
              isLoading={isLoading}
            >
              <NotSupported media={media} />
            </MediaRender>
          </View>
        </YStack>
      </AnimatePresence>
    </View>
  )
}
